import React, { useState, useEffect } from 'react';
import { Box, Text, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useConfig } from '../contexts/ConfigContext';
import axios from 'axios';
import BackButton from '../components/BackButton';
import PasswordStep from '../components/PasswordStep';
import { MIN_PASSWORD_LENGTH, ROUTES, API_ROUTES } from '../constants';

const FirstUserLoginPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [statusCheckError, setStatusCheckError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const { config } = useConfig();

  // Check user status on mount
  useEffect(() => {
    if (!supabase) {
      setStatusCheckError('Initialization error. Please refresh the page.');
      setIsCheckingStatus(false);
      return;
    }

    // Check if there's a hash in URL (invite link redirects with hash params)
    const hash = window.location.hash;
    const hasHashParams = hash && (hash.includes('access_token') || hash.includes('type=invite'));
    
    // Also check for query params (some Supabase flows use query params)
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokenParam = urlParams.has('token') && urlParams.get('type') === 'invite';
    const isInviteFlow = hasHashParams || hasTokenParam;
    
    // Set a timeout to check session if hash params are present (Supabase processes them asynchronously)
    let timeoutId: NodeJS.Timeout;
    let authStateSubscription: { unsubscribe: () => void } | null = null;

    const checkSessionAndStatus = async (session: any) => {
      if (!session) {
        // If we have hash params or token param, wait a bit longer for Supabase to process
        if (isInviteFlow) {
          return; // Will be called again when session is established
        }
        setStatusCheckError('Invalid or expired invite link. Please contact support.');
        setIsCheckingStatus(false);
        return;
      }

      // Clean up URL hash/query params after session is established
      if (isInviteFlow) {
        // Remove hash from URL
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        // Remove token query params if present
        if (hasTokenParam) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('token');
          newUrl.searchParams.delete('type');
          window.history.replaceState(null, '', newUrl.pathname + (newUrl.search || ''));
        }
      }

      // If user has a valid session, allow password setup
      // No need to check profile status - backend will handle validation when setting password
      // This allows users to set password regardless of their current status
      setIsCheckingStatus(false);
    };

    // Listen for auth state changes (catches when session is established from hash params)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle invite flow events
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || (session && event === 'INITIAL_SESSION')) {
        await checkSessionAndStatus(session);
      }
    });
    authStateSubscription = subscription;

    // Also check current session immediately
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.error('Session check error:', error);
        // If it's an invite flow, still allow password setup
        if (isInviteFlow) {
          // Wait a bit for Supabase to process the hash params
          timeoutId = setTimeout(async () => {
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession) {
              await checkSessionAndStatus(retrySession);
            } else {
              setStatusCheckError('Invalid or expired invite link. Please contact support.');
              setIsCheckingStatus(false);
            }
          }, 2000);
          return;
        }
        setStatusCheckError('Failed to verify invite link. Please contact support.');
        setIsCheckingStatus(false);
        return;
      }

      await checkSessionAndStatus(session);

      // If no session and we have hash params or token param, set a timeout to check again
      if (!session && isInviteFlow) {
        timeoutId = setTimeout(async () => {
          const { data: { session: retrySession } } = await supabase.auth.getSession();
          if (retrySession) {
            await checkSessionAndStatus(retrySession);
          } else {
            setStatusCheckError('Invalid or expired invite link. Please contact support.');
            setIsCheckingStatus(false);
          }
        }, 2000);
      }
    });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (authStateSubscription) {
        authStateSubscription.unsubscribe();
      }
    };
  }, [supabase, config, navigate]);

  // Validate password according to PasswordStep requirements
  const passwordValidation = {
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    hasMinLength: password.length >= MIN_PASSWORD_LENGTH,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    passwordsMatch: password === confirmPassword && password.length > 0
  };

  const isFormValid: boolean = 
    !!password && 
    !!confirmPassword && 
    passwordValidation.hasSpecialChar &&
    passwordValidation.hasMinLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasLowercase &&
    passwordValidation.passwordsMatch &&
    !isLoading;

  const handleSetPassword = async () => {
    if (!isFormValid) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      if (!config?.apiUrl) {
        throw new Error('API URL not configured');
      }

      const token = await supabase?.auth.getSession().then(res => res.data.session?.access_token);
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Call backend to set password and update status
      const response = await axios.post(
        `${config.apiUrl}${API_ROUTES.AUTH_FIRST_PASSWORD_SET}`,
        { password },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Password set successfully, redirect to login
        navigate(ROUTES.LOGIN, { 
          state: { 
            message: 'Password set successfully! Please log in with your new password.' 
          } 
        });
      }
    } catch (error: any) {
      console.error('Set password error:', error);
      if (error.response?.data?.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage('Failed to set password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking status
  if (isCheckingStatus) {
    return (
      <Box className="base-container">
        <Box className="wizard-main-content">
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          >
            <Text className="title-text">Verifying invite...</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  // Show error if status check failed
  if (statusCheckError) {
    return (
      <Box className="base-container">
        <Box className="wizard-main-content">
          <Box className="back-buttom-box">
            <BackButton onClick={() => navigate(ROUTES.LOGIN)} />
          </Box>
          <Box
            position="absolute"
            top="124px"
            left="20px"
            width="335px"
          >
            <Text className="title-text">Setup Error</Text>
          </Box>
          <Box
            position="absolute"
            top="180px"
            left="20px"
            width="335px"
          >
            <Text className="error-message">{statusCheckError}</Text>
          </Box>
          <Box
            className="button-big-container"
            top="316px"
          >
            <Button
              className="button-big"
              onClick={() => navigate(ROUTES.LOGIN)}
            >
              Go to Login
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Calculate top position for PasswordStep based on error message
  const passwordStepTopPosition: number = errorMessage ? 246 : 232;

  return (
    <Box className="base-container">
      <Box className="wizard-main-content">
        {/* Back Button */}
        <Box className="back-buttom-box">
          <BackButton onClick={() => navigate('/login')} />
        </Box>

        {/* Title */}
        <Box
          position="absolute"
          top="124px"
          left="20px"
          width="335px"
        >
          <Text className="title-text">Set Your Password</Text>
        </Box>

        {/* Description */}
        <Box
          position="absolute"
          top="164px"
          left="20px"
          width="335px"
        >
          <Text className="static-text">
            Please create a password to complete your account setup
          </Text>
        </Box>

        {/* Error Message */}
        {errorMessage && (
          <Box
            position="absolute"
            top="200px"
            left="20px"
            width="335px"
          >
            <Text className="error-message">{errorMessage}</Text>
          </Box>
        )}

        {/* Password Step Component */}
        <PasswordStep
          password={password}
          setPassword={(value) => {
            setPassword(value);
            if (errorMessage) setErrorMessage(null);
          }}
          confirmPassword={confirmPassword}
          setConfirmPassword={(value) => {
            setConfirmPassword(value);
            if (errorMessage) setErrorMessage(null);
          }}
          isFormValid={isFormValid}
          isLoading={isLoading}
          onNext={handleSetPassword}
          buttonText="Set Password"
          topPosition={passwordStepTopPosition}
          showButton={true}
        />
      </Box>
    </Box>
  );
};

export default FirstUserLoginPage;

